// Neighbor odor and noise (GAME-DESIGN.md §4). Kid-safe framing is part of the
// design, not decoration: the neighbor knocks first, warnings come before
// fines, and nothing ever happens to the birds.

import { clamp, round2 } from '../core/math';
import type { ChickenEntity, GameState, GridTile } from '../core/types';
import type { BiologyConfig } from './config';

/** Smell = Σ(soiled litter × hours uncleaned) × density. */
export function smellTotal(
  tiles: GridTile[],
  hoursUncleaned: number,
  birdCount: number,
  gridArea: number,
): number {
  const soil = tiles.reduce((sum, tile) => sum + tile.dirtinessLevel, 0);
  // Density factor: the same mess in a balcony is far worse than in a yard.
  const densityFactor = clamp(birdCount / Math.max(gridArea / 6, 1), 0.25, 4);
  return round2((soil / 100) * Math.min(hoursUncleaned, 72) * densityFactor);
}

export interface NoiseSources {
  roosters: number;
  uncollectedEggs: number;
  escapedOrDistressed: number;
}

export function noiseDb(sources: NoiseSources, config: BiologyConfig): number {
  const n = config.neighbors;
  return round2(
    sources.roosters * n.noiseRooster +
      sources.uncollectedEggs * n.noiseEggSong +
      sources.escapedOrDistressed * n.noiseEscapee,
  );
}

export function countNoiseSources(state: GameState): NoiseSources {
  const uncollectedEggs = state.structures.reduce(
    (sum, s) => sum + (s.state.eggs?.length ?? 0),
    0,
  );
  return {
    roosters: state.flock.filter((c) => c.gender === 'rooster' && c.stage === 'adult').length,
    uncollectedEggs,
    escapedOrDistressed: state.flock.filter(
      (c: ChickenEntity) =>
        c.currentState === 'ESCAPED' || c.conditions.some((cond) => cond.id === 'scared'),
    ).length,
  };
}

export type NeighborEvent = 'none' | 'knock' | 'warning' | 'fine';

export interface NeighborStepResult {
  event: NeighborEvent;
  /** Eggits deducted, if a fine was issued. */
  fine: number;
}

/**
 * Advances neighbor annoyance. Urban balconies are far more sensitive than
 * suburban yards — the housing choice has real consequences.
 */
export function stepNeighbors(
  state: GameState,
  hours: number,
  config: BiologyConfig,
): NeighborStepResult {
  const n = config.neighbors;
  const gridArea = state.grid.width * state.grid.height;
  const smell = smellTotal(state.grid.tiles, 24, state.flock.length, gridArea);
  const noise = noiseDb(countNoiseSources(state), config);
  const sensitivity = state.housing === 'balcony' ? 1.75 : 1;

  state.neighbor.smell = smell;
  state.neighbor.noiseDb = noise;

  let delta = -n.annoyanceDecayPerHour;
  if (smell > n.smellComplaintThreshold) delta += n.annoyancePerHourOverThreshold;
  // Sustained noise annoys; an occasional crow does not.
  if (noise > 60) delta += (noise - 60) / 20;
  // Not rounded: at 2 points per hour a Real-Time tick moves this 0.00056, and
  // rounding to 2dp put it straight back, so smell and noise never registered
  // for anyone playing in Real-Time. Same accumulator-rounding bug as elsewhere.
  state.neighbor.annoyance = clamp(
    state.neighbor.annoyance + delta * sensitivity * hours,
    0,
    100,
  );

  if (state.neighbor.annoyance >= n.warningAtAnnoyance) {
    // The friendly knock always comes before anything official.
    if (state.neighbor.warningsIssued === 0) {
      state.neighbor.warningsIssued++;
      return { event: 'knock', fine: 0 };
    }
    if (state.neighbor.warningsIssued === 1) {
      state.neighbor.warningsIssued++;
      return { event: 'warning', fine: 0 };
    }
    state.neighbor.finesIssued++;
    // Annoyance resets after a fine so it cannot cascade into a spiral.
    state.neighbor.annoyance = 50;
    return { event: 'fine', fine: n.fineEggits };
  }
  return { event: 'none', fine: 0 };
}
